import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ShieldWarning, Warning, GitBranch, ClipboardText, Lightbulb, ChartLineUp,
  ChartBar, Clock, Flag as FlagIcon, Link as LinkIcon,
  Compass, MagnifyingGlass, Files,
} from "@phosphor-icons/react";
import RiskPanel from "./project-tabs/RiskPanel";
import IssuesPanel from "./project-tabs/IssuesPanel";
import CRQAPanel from "./project-tabs/CRQAPanel";
import NCRPanel from "./project-tabs/NCRPanel";
import LessonsPanel from "./project-tabs/LessonsPanel";
import KPIPanel from "./project-tabs/KPIPanel";
import MPRPanel from "./project-tabs/MPRPanel";
import TimePanel from "./project-tabs/TimePanel";
import CloseoutPanel from "./project-tabs/CloseoutPanel";
import DocsPanel from "./project-tabs/DocsPanel";
import ConnectionsPanel from "./project-tabs/ConnectionsPanel";
import DesignInputsPanel from "./project-tabs/DesignInputsPanel";
import InternalAuditPanel from "./project-tabs/InternalAuditPanel";

/**
 * The 13 connected sub-tabs that surface real project data inside the
 * expanded project card. Each tab lives in its own file under
 * /components/app/project-tabs/*.jsx for maintainability.
 *
 * Tab badge counts are fetched in ONE round-trip via
 * GET /api/projects/{id}/tab-counts (added in v1.64), replacing 9
 * separate probes that fired on every card expand.
 */
const TAB_DEFS = [
  { id: "risk",        label: "Risk",          Icon: ShieldWarning,    Panel: RiskPanel },
  { id: "issues",      label: "Issues",        Icon: Warning,          Panel: IssuesPanel },
  { id: "crqa",        label: "CRQA",          Icon: GitBranch,        Panel: CRQAPanel },
  { id: "ncr",         label: "NCR",           Icon: ClipboardText,    Panel: NCRPanel },
  { id: "lessons",     label: "Lessons",       Icon: Lightbulb,        Panel: LessonsPanel },
  { id: "kpi",         label: "KPI",           Icon: ChartLineUp,      Panel: KPIPanel },
  { id: "mpr",         label: "MPR",           Icon: ChartBar,         Panel: MPRPanel },
  { id: "time",        label: "Time",          Icon: Clock,            Panel: TimePanel },
  { id: "closeout",    label: "Close-out",     Icon: FlagIcon,         Panel: CloseoutPanel },
  { id: "docs",        label: "Docs",          Icon: Files,            Panel: DocsPanel },
  { id: "connections", label: "Connections",   Icon: LinkIcon,         Panel: ConnectionsPanel },
  { id: "design",      label: "Design Inputs", Icon: Compass,          Panel: DesignInputsPanel },
  { id: "audit",       label: "Internal Audit", Icon: MagnifyingGlass, Panel: InternalAuditPanel },
];

export default function ProjectTabs({ project, onReload }) {
  const [active, setActive] = useState("risk");
  const [counts, setCounts] = useState({});

  // Batched count fetch — single round-trip
  useEffect(() => {
    if (!project?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${project.id}/tab-counts`);
        if (alive) setCounts(data || {});
      } catch {
        if (alive) setCounts({});
      }
    })();
    return () => { alive = false; };
  }, [project?.id]);

  const ActivePanel = TAB_DEFS.find((t) => t.id === active)?.Panel || (() => null);

  return (
    <section className="border-t border-slate-100 bg-slate-50/40" data-testid={`project-tabs-${project.id}`}>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-white px-4 py-2 overflow-x-auto">
        {TAB_DEFS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActive(id)}
            data-testid={`project-tab-${id}-${project.id}`}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition whitespace-nowrap ${
              active === id
                ? "border-[#1E5CBA] bg-blue-50 text-[#1E5CBA]"
                : "border-transparent text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon size={12} weight={active === id ? "fill" : "regular"} />
            {label}
            {counts[id] != null && counts[id] > 0 && (
              <span className={`ml-0.5 inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                active === id ? "bg-[#1E5CBA] text-white" : "bg-slate-200 text-slate-700"
              }`}>{counts[id]}</span>
            )}
          </button>
        ))}
      </div>
      <div className="px-4 py-3">
        <ActivePanel project={project} onReload={onReload} />
      </div>
    </section>
  );
}
